import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Order } from '../../models/order.models';
import { TunnelWashDetailDisplay, describeTunnelWashDetail } from '../../utils/tunnel-wash.util';
import { formatDateTimeWithTimezone } from '../../../shared/utils/date.util';

/**
 * Tunnel wash package, status and time of a tunnel board row (CWS-1921). Shown in the
 * board's detail panel and in the order details dialog; renders nothing when the row
 * has no tunnel wash state.
 */
@Component({
  selector: 'app-tunnel-wash-summary',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tunnel-wash-summary.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tunnel-wash-summary.component.scss'],
})
export class TunnelWashSummaryComponent implements OnChanges, OnDestroy {
  @Input() order: Order | null = null;
  /**
   * The board's clock, so "no confirmation" appears on time. Without it (the details
   * dialog) the component keeps its own 1 s clock while the wash is pending.
   */
  @Input() nowMs: number | null = null;

  display: TunnelWashDetailDisplay | null = null;

  private static readonly OWN_CLOCK_MS = 1000;
  private readonly cdr = inject(ChangeDetectorRef);
  private ownClock: ReturnType<typeof setInterval> | null = null;

  ngOnChanges(): void {
    this.recompute();
    const needsOwnClock = this.nowMs === null && this.order?.tunnelWashStatus === 'pending';
    if (needsOwnClock) {
      this.startOwnClock();
    } else {
      this.stopOwnClock();
    }
  }

  ngOnDestroy(): void {
    this.stopOwnClock();
  }

  private recompute(): void {
    this.display = this.order
      ? describeTunnelWashDetail(this.order, this.nowMs ?? Date.now(), formatDateTimeWithTimezone)
      : null;
  }

  private startOwnClock(): void {
    if (this.ownClock !== null) {
      return;
    }
    this.ownClock = setInterval(() => {
      this.recompute();
      this.cdr.markForCheck();
    }, TunnelWashSummaryComponent.OWN_CLOCK_MS);
  }

  private stopOwnClock(): void {
    if (this.ownClock !== null) {
      clearInterval(this.ownClock);
      this.ownClock = null;
    }
  }
}
